import type { FileUploadSession } from "@/db/fileUploadDb";

/**
 * LSP-19865: a failed pre-persist must not leave its reserved S3 multipart upload behind.
 *
 * The dialog reserves the multipart upload (POST /contact/uploads) BEFORE it copies the file into
 * IndexedDB. When that copy fails, the cleanup path has to answer one question before it may abort
 * the reservation: did a durable session row for this target land anyway (in which case the drain
 * owns the bytes and an abort would destroy a resumable upload)? That question is asked of the same
 * IndexedDB that just failed, so on the incident path every answer was "I don't know":
 *
 *  - the 50 ms lookup timed out, the 5 s late lookup then REJECTED (`IdbStallError`), and the
 *    `.catch` marked the row failed without ever issuing the abort (staging, 2026-09-04: session
 *    `a0b29550…` sat `pending` with zero parts; contact-api deferred it every minute inside the
 *    24-hour multipart stall window while the conversation showed a pending-recording placeholder);
 *  - the late lookup's own deadline expired with the lookup still hanging;
 *  - the synchronous lookup rejected outright;
 *  - the abort itself timed out and later rejected.
 *
 * Each of those exits is an "unproven" cleanup: not proof of a handoff, not proof of an abort. This
 * keeps ONE bounded loop per target over those exits: every tick re-asks IndexedDB, and the first
 * definite answer decides — a resumable row is adopted by the dialog's ordinary deferred path, a
 * completed row needs nothing, an absent or cancelled row means the reservation is aborted (retried
 * across ticks), and a parked row (`needs-attention`, `recovery-blocked`) is left to the paths that
 * own those states — its target still backs bytes the user may retry. Past the deadline it gives up and says so; the server's own stall
 * window remains the backstop it always was.
 *
 * Deliberately pure: every side effect is injected so the loop is unit-tested with fake timers.
 * Silent-failure rule: every exit names itself through `log`.
 */
export const PRE_PERSIST_TARGET_RECONCILE_INTERVAL_MS = 15_000;
export const PRE_PERSIST_TARGET_RECONCILE_LIFETIME_MS = 15 * 60_000;
export const PRE_PERSIST_TARGET_ABORT_MAX_ATTEMPTS = 5;
/**
 * Bound on one abort attempt. The abort request itself carries no timeout (a server that accepts the
 * connection and goes silent can hold it for minutes), and an unbounded await inside a tick would
 * silently defeat the lifetime, the attempt budget and any re-arm (review finding).
 */
export const PRE_PERSIST_TARGET_ABORT_TIMEOUT_MS = 30_000;

/**
 * Thrown by a caller's `abortTarget` when it gave up WAITING on a request that may still complete
 * (a hung connection). Counted as a timed-out attempt, never as a definitive rejection, so adoption
 * stays refused: the reservation may be gone.
 */
export class PrePersistAbortUndecidedError extends Error {
	constructor(message = "Multipart abort still pending; its outcome is undecided") {
		super(message);
		this.name = "PrePersistAbortUndecidedError";
	}
}

export type PrePersistTargetReconcileEvent =
	| "started"
	| "lookup-failed"
	| "row-offered"
	| "row-adopted"
	| "row-adoption-refused"
	| "row-tombstoned"
	| "row-completed"
	| "row-left-alone"
	| "target-aborted"
	| "abort-failed"
	| "abort-timed-out"
	| "abort-settled-after-stop"
	| "abort-exhausted"
	| "no-longer-unresolved"
	| "gave-up"
	| "stopped";

export type PrePersistTargetReconcileOutcome =
	| "adopted"
	| "completed"
	| "left-alone"
	| "aborted"
	| "abort-exhausted"
	| "no-longer-unresolved"
	| "gave-up"
	| "stopped";

export interface PrePersistTargetReconcileOptions {
	/** The opaque multipart target id the failed pre-persist reserved; also the durable row's `uploadId`. */
	uploadId: string;
	fileName?: string;
	/** Which unproven exit armed this loop. Logged, never branched on. */
	reason: string;
	lookupSession(uploadId: string): Promise<FileUploadSession | undefined>;
	abortTarget(): Promise<void>;
	isDeliverableByResume(status: FileUploadSession["status"]): boolean;
	/**
	 * The entry still has neither a durable row nor a confirmed abort, and no other owner (a user
	 * cancellation, a late lookup that landed after all) has taken over. Checked before every tick.
	 */
	isStillUnresolved(): boolean;
	/** A resumable row exists after all: the dialog adopts it exactly as the late lookup would have. */
	onAdopt(session: FileUploadSession): void | Promise<void>;
	/**
	 * A resumable row exists but the reservation it points at has been destroyed by this loop's abort
	 * (or an abort attempt timed out, so the reservation's state is unknown). The dialog records the
	 * row's identity and tombstones it so no drain drives the dead target.
	 */
	onTombstone?(session: FileUploadSession): void | Promise<void>;
	/** The abort went through: the dialog records it so a later user cancel does not abort twice. */
	onAborted(): void;
	log(event: PrePersistTargetReconcileEvent, details: Record<string, unknown>): void;
	intervalMs?: number;
	/** Delay before the first tick; defaults to `intervalMs`. */
	initialDelayMs?: number;
	lifetimeMs?: number;
	maxAbortAttempts?: number;
	/** Bound on a single `abortTarget()` call; a timeout counts as a failed attempt. */
	abortTimeoutMs?: number;
	now?: () => number;
}

export interface PrePersistTargetReconcileHandle {
	readonly uploadId: string;
	readonly outcome: PrePersistTargetReconcileOutcome | undefined;
	/**
	 * Hand the loop a row another reader found (the dialog's original late lookup) so it decides NOW
	 * under its own rule instead of waiting for its next tick. Serialized behind a tick in flight. A
	 * FINISHED loop still decides: after a confirmed abort the row is tombstoned (its reservation is
	 * gone); after a loop that ended without a confirmed abort the row is adopted only if every abort
	 * attempt was definitively rejected, else left to the drain. The caller never adopts.
	 */
	offer(session: FileUploadSession | undefined): Promise<void>;
	stop(): void;
	/** Resolves once the tick in flight, if any, has settled. Test seam. */
	settle(): Promise<void>;
}

export function reconcilePrePersistTarget(options: PrePersistTargetReconcileOptions): PrePersistTargetReconcileHandle {
	const intervalMs = options.intervalMs ?? PRE_PERSIST_TARGET_RECONCILE_INTERVAL_MS;
	const initialDelayMs = options.initialDelayMs ?? intervalMs;
	const lifetimeMs = options.lifetimeMs ?? PRE_PERSIST_TARGET_RECONCILE_LIFETIME_MS;
	const maxAbortAttempts = options.maxAbortAttempts ?? PRE_PERSIST_TARGET_ABORT_MAX_ATTEMPTS;
	const abortTimeoutMs = options.abortTimeoutMs ?? PRE_PERSIST_TARGET_ABORT_TIMEOUT_MS;
	const now = options.now ?? (() => Date.now());
	const startedAt = now();
	const deadlineAt = startedAt + lifetimeMs;

	let outcome: PrePersistTargetReconcileOutcome | undefined;
	let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
	let tickInFlight: Promise<void> | undefined;
	let abortAttempts = 0;
	let abortRejections = 0;
	let lookupFailures = 0;
	/**
	 * Adoption is safe only while every abort attempt this loop ever issued was DEFINITIVELY rejected
	 * (the server refused, so the reservation is alive). A resolved attempt destroyed it; a timed-out
	 * attempt may still be destroying it. Either poisons adoption for the rest of the loop's life —
	 * the single rule that keeps "adopt" and "abort" from racing (review rounds 1-4).
	 */
	const adoptionAllowed = () => abortAttempts === abortRejections;

	const log = (event: PrePersistTargetReconcileEvent, details: Record<string, unknown> = {}) => {
		try {
			options.log(event, {
				uploadId: options.uploadId,
				fileName: options.fileName,
				reason: options.reason,
				elapsedMs: Math.max(0, now() - startedAt),
				...details
			});
		} catch {
			// A diagnostic must never change an upload's outcome.
		}
	};
	const finish = (finalOutcome: PrePersistTargetReconcileOutcome) => {
		outcome = finalOutcome;
		if (timer !== undefined) {
			globalThis.clearTimeout(timer);
			timer = undefined;
		}
	};
	const schedule = (delayMs: number) => {
		if (outcome !== undefined || timer !== undefined) return;
		timer = globalThis.setTimeout(() => {
			timer = undefined;
			tickInFlight = tick().finally(() => {
				tickInFlight = undefined;
				if (outcome === undefined) schedule(intervalMs);
			});
		}, delayMs);
	};

	const tryAbort = async (rowStatus: FileUploadSession["status"] | "absent", rowToTombstone?: FileUploadSession): Promise<void> => {
		abortAttempts += 1;
		let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
		try {
			const timedOut = new Promise<"timed-out">((resolve) => {
				timeoutId = globalThis.setTimeout(() => resolve("timed-out"), abortTimeoutMs);
			});
			const settled = await Promise.race([options.abortTarget().then(() => "settled" as const), timedOut]);
			if (settled === "timed-out") {
				// The request may still complete later; the caller decides whether to reuse or replace it.
				// Here it is a failed attempt like any other, so the budget, the lifetime and re-arming
				// all keep meaning while the connection hangs.
				log("abort-timed-out", { rowStatus, abortAttempts, maxAbortAttempts, abortTimeoutMs });
				if (abortAttempts >= maxAbortAttempts) {
					log("abort-exhausted", { rowStatus, abortAttempts });
					finish("abort-exhausted");
				}
				return;
			}
		} catch (error) {
			if (error instanceof PrePersistAbortUndecidedError) {
				log("abort-timed-out", { rowStatus, abortAttempts, maxAbortAttempts, source: "caller", error: describeError(error) });
			} else {
				abortRejections += 1;
				log("abort-failed", { rowStatus, abortAttempts, maxAbortAttempts, error: describeError(error) });
			}
			if (abortAttempts >= maxAbortAttempts) {
				log("abort-exhausted", { rowStatus, abortAttempts });
				finish("abort-exhausted");
			}
			return;
		} finally {
			if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
		}
		if (outcome !== undefined) {
			// stop() ran while the request was in flight. The abort is durable server-side, but the owner
			// has torn down (unmount); do not write into its bookkeeping or overwrite the recorded outcome.
			log("abort-settled-after-stop", { rowStatus, abortAttempts, recordedOutcome: outcome });
			return;
		}
		if (rowToTombstone) {
			try {
				await options.onTombstone?.(rowToTombstone);
				log("row-tombstoned", { sessionId: rowToTombstone.id, status: rowToTombstone.status });
			} catch (error) {
				// The abort is durable server-side either way; the drain's own dead-target handling remains.
				log("row-tombstoned", { sessionId: rowToTombstone.id, tombstoneError: describeError(error) });
			}
		}
		try {
			options.onAborted();
		} catch {
			// The abort is already durable server-side; a bookkeeping failure must not re-run it.
		}
		log("target-aborted", { rowStatus, abortAttempts, lookupFailures });
		finish("aborted");
	};

	const tick = (): Promise<void> => decide(() => options.lookupSession(options.uploadId), "lookup");

	const decide = async (readSession: () => Promise<FileUploadSession | undefined>, source: "lookup" | "offer"): Promise<void> => {
		if (outcome !== undefined) return;
		if (!safeIsStillUnresolved()) {
			log("no-longer-unresolved", { lookupFailures, abortAttempts });
			finish("no-longer-unresolved");
			return;
		}
		if (now() >= deadlineAt) {
			log("gave-up", { lookupFailures, abortAttempts, lifetimeMs });
			finish("gave-up");
			return;
		}
		let session: FileUploadSession | undefined;
		try {
			session = await readSession();
			if (source === "offer") log("row-offered", { sessionId: session?.id, status: session?.status });
		} catch (error) {
			lookupFailures += 1;
			log("lookup-failed", { lookupFailures, error: describeError(error) });
			return;
		}
		if (outcome !== undefined) return;
		// Re-check AFTER the read: a user cancel or a late lookup that adopted the row can land while
		// the lookup was in flight, and acting on the pre-read snapshot would abort a row that was just
		// adopted, or adopt one the user just cancelled (review finding).
		if (!safeIsStillUnresolved()) {
			log("no-longer-unresolved", { lookupFailures, abortAttempts, phase: "after-lookup" });
			finish("no-longer-unresolved");
			return;
		}
		if (session && options.isDeliverableByResume(session.status)) {
			if (!adoptionAllowed()) {
				// The row is resumable on paper, but this loop already tried to destroy its reservation and
				// cannot prove it failed. Adopting would render "finishing" for an upload nothing can finish;
				// keep aborting instead and tombstone the row once the abort is confirmed.
				log("row-adoption-refused", { sessionId: session.id, status: session.status, abortAttempts, abortRejections });
				await tryAbort(session.status, session);
				return;
			}
			try {
				await options.onAdopt(session);
			} catch (error) {
				// Adoption is the dialog's bookkeeping; if it throws, the row still exists and the drain owns
				// it. Aborting now would destroy a resumable upload, so this exit is recorded as adopted.
				log("row-adopted", { sessionId: session.id, status: session.status, adoptError: describeError(error) });
				finish("adopted");
				return;
			}
			log("row-adopted", { sessionId: session.id, status: session.status, lookupFailures });
			finish("adopted");
			return;
		}
		if (session?.status === "complete") {
			log("row-completed", { sessionId: session.id });
			finish("completed");
			return;
		}
		if (session && session.status !== "cancelled") {
			log("row-left-alone", { sessionId: session.id, status: session.status });
			finish("left-alone");
			return;
		}
		await tryAbort(session?.status ?? "absent");
	};

	/**
	 * A row offered after the loop finished. The loop's final state still decides: a confirmed abort
	 * means the reservation is destroyed, so the row must be tombstoned rather than left for a drain to
	 * drive (review finding); a loop that ended without a confirmed abort adopts only under the same
	 * rule as a live tick, and otherwise leaves the row to the drain's own dead-target handling.
	 */
	const decideAfterFinish = async (session: FileUploadSession | undefined): Promise<void> => {
		if (!session) return;
		log("row-offered", { sessionId: session.id, status: session.status, afterOutcome: outcome });
		if (outcome === "aborted") {
			try {
				await options.onTombstone?.(session);
				log("row-tombstoned", { sessionId: session.id, status: session.status, afterOutcome: outcome });
			} catch (error) {
				log("row-tombstoned", { sessionId: session.id, afterOutcome: outcome, tombstoneError: describeError(error) });
			}
			return;
		}
		const loopEndedUndecided = outcome === "gave-up" || outcome === "abort-exhausted";
		if (loopEndedUndecided && adoptionAllowed() && options.isDeliverableByResume(session.status) && safeIsStillUnresolved()) {
			try {
				await options.onAdopt(session);
			} catch (error) {
				log("row-adopted", {
					sessionId: session.id,
					status: session.status,
					afterOutcome: outcome,
					adoptError: describeError(error)
				});
				return;
			}
			log("row-adopted", { sessionId: session.id, status: session.status, afterOutcome: outcome });
			return;
		}
		log("row-left-alone", { sessionId: session.id, status: session.status, afterOutcome: outcome, abortAttempts, abortRejections });
	};

	const safeIsStillUnresolved = (): boolean => {
		try {
			return options.isStillUnresolved();
		} catch {
			// An unreadable predicate must not strand the target: keep reconciling.
			return true;
		}
	};

	log("started", { intervalMs, initialDelayMs, lifetimeMs, maxAbortAttempts });
	schedule(initialDelayMs);

	return {
		uploadId: options.uploadId,
		get outcome() {
			return outcome;
		},
		async offer(session) {
			if (outcome !== undefined) {
				await decideAfterFinish(session);
				return;
			}
			// Behind any tick in flight, then as a tick of its own so the schedule stays single-flight.
			// Having waited, the offered snapshot is stale — the tick that just ran may have aborted the
			// target, or another actor may have completed or parked the row — so the decision re-reads
			// the row instead of trusting the snapshot, and a loop that finished meanwhile still decides
			// (review finding).
			const waited = tickInFlight !== undefined;
			await tickInFlight;
			const readOffered = waited ? () => options.lookupSession(options.uploadId) : () => Promise.resolve(session);
			if (outcome !== undefined) {
				// A re-read that fails must not drop the row: the offered snapshot is still a durable row this
				// loop's verdict applies to, so decide from it exactly as the no-wait path does (review finding).
				let fresh: FileUploadSession | undefined = session;
				try {
					fresh = await readOffered();
				} catch (error) {
					log("lookup-failed", {
						lookupFailures,
						error: describeError(error),
						phase: "offer-after-finish",
						decidingFromOfferedSnapshot: true
					});
				}
				await decideAfterFinish(fresh);
				return;
			}
			if (timer !== undefined) {
				globalThis.clearTimeout(timer);
				timer = undefined;
			}
			tickInFlight = decide(readOffered, "offer").finally(() => {
				tickInFlight = undefined;
				if (outcome === undefined) schedule(intervalMs);
			});
			await tickInFlight;
		},
		stop() {
			if (outcome !== undefined) return;
			log("stopped", { lookupFailures, abortAttempts });
			finish("stopped");
		},
		async settle() {
			await tickInFlight;
		}
	};
}

function describeError(error: unknown): { name?: string; message?: string } {
	if (error instanceof Error) return { name: error.name, message: error.message };
	return { message: String(error) };
}
